import { AnimatePresence, motion } from "motion/react";
import type React from "react";
import { useId, useRef } from "react";
import { twJoin } from "tailwind-merge";

import { type FormDocument, useDocumentInput } from "@/components/DocumentInput/useDocumentInput";
import { FileAttachment01Icon } from "@/components/Icons/FileAttachment01Icon";
import { type FormImage, useImageInput } from "@/components/ImageInput/useImageInput";
import { MediaXButton } from "@/components/Media/MediaXButton";
import { Pdf } from "@/components/Pdf/Pdf";
import { VideoFilePreview } from "@/components/VideoFilePreview/VideoFilePreview";
import type { FormVideo } from "@/components/VideoInput/useVideoInput";
import { isVideoUploaded, useVideoInput } from "@/components/VideoInput/useVideoInput";
import { buttonVariants } from "@/design-system/Button";
import { FileInput } from "@/design-system/FileInput";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { ALLOWED_FILE_TYPES } from "@/helpers/file-types";
import { useUploadVideo } from "@/hooks/Network/useUploadVideo";
import { useTranslation } from "@/translations";

import { ClapperboardIcon } from "../Icons/ClapperboardIcon";
import { Image01Icon } from "../Icons/Image01Icon";

export type MediaPickerButtonProps<TValue> = {
  name?: string;
  value: TValue;
  disabled?: boolean;
  maxFiles?: number;
  isError?: boolean;
  onChange: (files: TValue) => void;
};

export function ImagePickerButton({
  name,
  value,
  maxFiles = 1,
  disabled,
  isError,
  onChange,
}: MediaPickerButtonProps<FormImage[]>): React.ReactNode {
  const id = useId();
  const refInput = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const { addImages, removeImage } = useImageInput({
    selectedImages: value,
    maximumFiles: maxFiles,
    onChange,
  });
  const handleAddImages = (files: FileList | null) => {
    if (!files) return;
    addImages(files);
  };
  const handleDeleteImage = (index: number) => {
    removeImage(value[index]);

    // Clear input valueo to maintain single source of truth
    if (refInput.current) {
      refInput.current.value = "";
    }
  };

  return (
    <Stack gap="sm">
      <label
        className={twJoin(
          buttonVariants({
            variant: "soft",
            type: "label",
            size: "sm",
          }),
          isError && "border-border-danger! focus-visible:ring-4 focus-visible:ring-border-danger-tertiary",
        )}
        aria-invalid={isError}
        htmlFor={id}
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
      >
        <Image01Icon />
        <span className="leading-1">
          {t("component.media-picker-button.add-image", {
            count: maxFiles,
          })}
        </span>
      </label>
      <FileInput
        ref={refInput}
        aria-invalid={isError}
        data-testid="image-input"
        tabIndex={-1}
        accept={ALLOWED_FILE_TYPES.IMAGE}
        multiple={maxFiles > 1}
        disabled={disabled}
        onChange={(e) => handleAddImages(e.target.files)}
        {...{
          id,
          name,
        }}
      />
      {value.length > 0 && (
        <Inline gap="sm">
          <AnimatePresence>
            {value.map((image, index) => (
              <motion.div
                variants={{
                  initial: {
                    opacity: 0,
                  },
                  animate: {
                    opacity: 1,
                  },
                  exit: {
                    opacity: 0,
                  },
                }}
                transition={{
                  duration: 0.1,
                  ease: "linear",
                }}
                initial="initial"
                animate="animate"
                exit="exit"
                key={image.url}
                className="relative h-media-input max-w-media-action-label"
              >
                <span className="absolute top-0 right-0 translate-x-1/3 -translate-y-1/3">
                  <MediaXButton onClick={() => handleDeleteImage(index)} title={t("common.action.delete")} />
                </span>
                <img src={image.url} className="h-full rounded-md object-cover" />
              </motion.div>
            ))}
          </AnimatePresence>
        </Inline>
      )}
    </Stack>
  );
}

type VideoPickerButtonProps = MediaPickerButtonProps<FormVideo[]> & {
  shouldSkipProcessing?: boolean;
};

export function VideoPickerButton({
  name,
  value,
  disabled,
  maxFiles = 1,
  isError,
  shouldSkipProcessing,
  onChange,
}: VideoPickerButtonProps): React.ReactNode {
  const id = useId();
  const refInput = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const { uploadFormVideo } = useUploadVideo({
    onProgress: ({ name: fileName, progress }) => {
      onChange(
        [...value].map((video) => {
          if (!isVideoUploaded(video) && video.file.name === fileName) {
            return {
              ...video,
              uploadProgress: progress,
            };
          }

          return video;
        }),
      );
    },
    shouldProcess: !shouldSkipProcessing,
  });
  const { addVideos, removeVideo } = useVideoInput({
    selectedVideos: value,
    maximumFiles: maxFiles,
    onChange,
    uploadFn: uploadFormVideo,
  });
  const handleAddVideos = (files: FileList | null) => {
    if (!files) return;
    addVideos(files);
  };
  const handleDeleteVideo = (index: number) => {
    removeVideo(value[index]);

    // Clear input valueo to maintain single source of truth
    if (refInput.current) {
      refInput.current.value = "";
    }
  };

  return (
    <Stack gap="sm">
      <label
        className={twJoin(
          buttonVariants({
            variant: "soft",
            type: "label",
            size: "sm",
          }),
          isError && "border-border-danger! focus-visible:ring-4 focus-visible:ring-border-danger-tertiary",
        )}
        aria-invalid={isError}
        htmlFor={id}
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
      >
        <ClapperboardIcon />
        <span className="leading-1">
          {t("component.media-picker-button.add-video", {
            count: maxFiles,
          })}
        </span>
      </label>
      <FileInput
        ref={refInput}
        aria-invalid={isError}
        data-testid="video-input"
        tabIndex={-1}
        accept={ALLOWED_FILE_TYPES.VIDEO}
        disabled={disabled}
        onChange={(e) => handleAddVideos(e.target.files)}
        {...{
          id,
          name,
        }}
      />
      {value.length > 0 && (
        <Inline gap="sm">
          <AnimatePresence>
            {value.map((video, index) => (
              <VideoFilePreview key={video.url} video={video} onDelete={() => handleDeleteVideo(index)} isPlayable />
            ))}
          </AnimatePresence>
        </Inline>
      )}
    </Stack>
  );
}
export function DocumentPickerButton({
  name,
  value,
  disabled,
  maxFiles = 1,
  isError,
  onChange,
}: MediaPickerButtonProps<FormDocument[]>): React.ReactNode {
  const id = useId();
  const refInput = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const { addDocuments, removeDocument } = useDocumentInput({
    selectedDocuments: value,
    maximumFiles: maxFiles,
    onChange,
  });
  const handleAddDocuments = (files: FileList | null) => {
    if (!files) return;
    addDocuments(files);
  };
  const handleDeleteDocument = (index: number) => {
    removeDocument(value[index]);

    // Clear input valueo to maintain single source of truth
    if (refInput.current) {
      refInput.current.value = "";
    }
  };

  return (
    <Stack gap="sm">
      <label
        className={twJoin(
          buttonVariants({
            variant: "soft",
            type: "label",
            size: "sm",
          }),
          isError && "border-border-danger! focus-visible:ring-4 focus-visible:ring-border-danger-tertiary",
        )}
        aria-invalid={isError}
        htmlFor={id}
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
      >
        <FileAttachment01Icon />
        <span className="leading-1">
          {t("component.media-picker-button.add-document", {
            count: maxFiles,
          })}
        </span>
      </label>
      <FileInput
        ref={refInput}
        aria-invalid={isError}
        data-testid="document-input"
        tabIndex={-1}
        accept={ALLOWED_FILE_TYPES.DOCUMENT}
        multiple
        disabled={disabled}
        onChange={(e) => handleAddDocuments(e.target.files)}
        {...{
          id,
          name,
        }}
      />
      {value.length > 0 && (
        <Inline gap="sm">
          {value.map((document, index) => {
            const fileName = "file" in document ? document.file.name : document.fileName;
            const fileSize = "file" in document ? document.file.size : undefined;

            return (
              <Pdf
                key={fileName}
                fileName={fileName}
                fileSize={fileSize}
                onDelete={() => handleDeleteDocument(index)}
              />
            );
          })}
        </Inline>
      )}
    </Stack>
  );
}
